import { isIndustryPageKey, type IndustryPageKey } from '../content/industryPages.js';
import type { LandingLocale } from '../content/landingContentTypes.js';
import type { StaticPageAudience, StaticPageContent } from '../content/staticPageContent.js';

const previousTitles: Partial<Record<IndustryPageKey, Record<StaticPageAudience, readonly string[]>>> = {
  "pflege": {
    "applicant": [
      "Pflege braucht Menschen mit Zukunft.",
      "Care needs people with a future.",
      "Bakımın geleceği insanla başlar."
    ],
    "company": [
      "Ihr Pflegeteam braucht Menschen mit Zukunft.",
      "Your care team needs people with a future.",
      "Bakım ekibinizin geleceği insanla başlar."
    ]
  },
  "einzelhandel": {
    "applicant": [
      "Einzelhandel. Service, Frische und Kundennähe.",
      "Retail. Service, fresh goods and customer closeness.",
      "Perakende. Hizmet, tazelik ve müşteri yakınlığı."
    ],
    "company": [
      "Ihr Einzelhandel braucht Menschen mit Gespür.",
      "Your retail business needs people with passion.",
      "Mağazanızın güçlü çalışanlara ihtiyacı var."
    ]
  },
  "handwerk": {
    "applicant": [
      "Handwerk braucht Menschen mit Zukunft.",
      "Build a future-ready path in the trades.",
      "Zanaatte geleceği olan bir yol kur."
    ],
    "company": [
      "Ihr Handwerk braucht Menschen mit Können.",
      "Your trade business needs skilled people.",
      "İşletmenizin deneyimli ustalara ihtiyacı var."
    ]
  },
  "gastro": {
    "applicant": [
      "Dein Start in starken Service-Teams.",
      "Your start in strong service teams.",
      "Güçlü servis ekiplerinde yeni başlangıcın."
    ],
    "company": [
      "Ihr Gastroteam braucht Menschen mit Leidenschaft.",
      "Your hospitality team needs people with passion.",
      "Gastronomi işletmenizin güçlü bir ekibe ihtiyacı var."
    ]
  },
  "elektriker": {
    "applicant": [
      "Deine Zukunft als Elektriker oder Elektroniker.",
      "Your future in electrical & electronics work.",
      "Elektrik ve elektronikte geleceğin."
    ],
    "company": [
      "Ihr Elektroteam braucht Fachkräfte mit Können.",
      "Your electrical business needs skilled people.",
      "Elektrik işletmenizin uzmanlara ihtiyacı var."
    ]
  },
  "bauwesen": {
    "applicant": [
      "Bauwesen. Baue an deiner Zukunft.",
      "Construction. Build your future.",
      "İnşaat. Geleceğini sağlam temellerle kur."
    ],
    "company": [
      "Ihr Bauteam braucht Menschen, die anpacken.",
      "Your construction team needs people who deliver.",
      "İnşaat ekibinizin işini bilen insanlara ihtiyacı var."
    ]
  },
  "schweisser": {
    "applicant": [
      "Schweißer. Dein Handwerk verbindet.",
      "Welding. Your craft makes connections.",
      "Kaynakçılık. Ustalığınla sağlam bağlar kur."
    ],
    "company": [
      "Ihr Schweißbetrieb braucht Menschen, die verbinden.",
      "Your welding business needs people who connect.",
      "Kaynak işletmenizin nitelikli çalışanlara ihtiyacı var."
    ]
  },
  "cnc": {
    "applicant": [
      "CNC-Zerspanung. Präzision mit Zukunft.",
      "CNC machining. Precision with a future.",
      "CNC ve talaşlı imalat. Hassasiyetle geleceğe."
    ],
    "company": [
      "Ihr CNC-Betrieb braucht Menschen mit Präzision.",
      "Your CNC business needs people with precision.",
      "CNC işletmenizin hassas çalışan ustalara ihtiyacı var."
    ]
  },
  "shk": {
    "applicant": [
      "Deine Zukunft in Sanitär, Heizung & Klima.",
      "Your future in plumbing, heating & cooling.",
      "Sıhhi tesisat, ısıtma ve klimada geleceğin."
    ],
    "company": [
      "Ihr SHK-Betrieb braucht Fachkräfte mit Können.",
      "Your plumbing and HVAC business needs skilled people.",
      "Tesisat işletmenizin uzman çalışanlara ihtiyacı var."
    ]
  },
  "lkw-fahrer": {
    "applicant": [
      "LKW-Fahrer. Bring deine Zukunft auf die Straße.",
      "Truck driving. Take your future on the road.",
      "Kamyon şoförlüğü. Geleceğini yola çıkar."
    ],
    "company": [
      "Ihr Fuhrpark braucht Menschen mit Verantwortung.",
      "Your fleet needs people you can rely on.",
      "Filonuzun güvenilir sürücülere ihtiyacı var."
    ]
  }
};

export const isPreviousThreeLineIndustryHeading = (key: string, title: string, audience: StaticPageAudience): boolean =>
  isIndustryPageKey(key) && (previousTitles[key]?.[audience].includes(title) ?? false);

// Replace an obsolete keyword only after a shipped heading has been refreshed.
export const refreshIndustryHeroHighlight = (
  page: StaticPageContent,
  defaults: StaticPageContent,
  locale: LandingLocale,
  contentRevision: number,
): void => {
  if (contentRevision >= 27 || !isIndustryPageKey(page.key) || page.title !== defaults.title) return;
  const highlight = page.heroTitleHighlight;
  if (highlight && !page.title.toLocaleLowerCase(locale).includes(highlight.toLocaleLowerCase(locale)))
    page.heroTitleHighlight = defaults.heroTitleHighlight;
};
